/**
 * Source configuration Yevhen edits himself (ticket body): Slack channels and
 * people, Linear views, known senders, live customer titles. Stored in
 * Firestore `helm_config/{env}` and read by the collectors.
 *
 * One text-area idiom (`id|label` per line, or one value per line) covers
 * every repeated field rather than bespoke add/remove rows per group — the
 * config groups are all "a handful of id/label pairs" or "a list of
 * strings", and Yevhen edits this rarely enough that a compact text form
 * beats five different widget shapes for the one-off case.
 */
import { useState } from 'react';
import type React from 'react';
import {
  Button,
  Field,
  Label,
  Select,
  Switch,
  SwitchField,
  Text,
  Textarea,
} from '@a7y/tailwind-catalyst';
import { DEFAULT_HELM_SOURCE_CONFIG, HELM_CARD_STYLES } from '@a7y/platform-health/helm';
import type { HelmCardStyle, HelmSourceConfig } from '@a7y/platform-health/helm';

function linesOf(text: string): string[] {
  return text
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);
}

function pairsToText(pairs: { id: string; label: string }[]): string {
  return pairs.map((p) => `${p.id}|${p.label}`).join('\n');
}

/**
 * A line typed as a bare id (no `|label`) used to be
 * saved with the id itself as the label — `resolveUserName` in
 * `scripts/helm/collectors/slack.cjs` returns a configured entry's `label`
 * unconditionally once it finds the id, so that fabricated label reached the
 * card as a person's or channel's display name with no disclosure. Leaving
 * the label genuinely empty here means `configured.label` resolves to `''`,
 * which every consumer already treats as falsy and falls through to the
 * real lookup (`users.info` for people, `conversations.info` for channels)
 * — never a stored id standing in for a name.
 */
function textToPairs(text: string): { id: string; label: string }[] {
  return linesOf(text).map((line) => {
    const [id, ...rest] = line.split('|');
    return { id: id.trim(), label: rest.join('|').trim() };
  });
}

function ConfigField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (next: string) => void;
}) {
  return (
    <Field>
      <Label className="text-xs text-zinc-500">{label}</Label>
      <Textarea
        rows={3}
        value={value}
        onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => onChange(e.target.value)}
      />
    </Field>
  );
}

export function SourceConfigEditor({
  config,
  isDefault,
  saving,
  onSave,
}: {
  config: HelmSourceConfig;
  isDefault: boolean;
  saving: boolean;
  onSave: (config: HelmSourceConfig) => void;
}) {
  const [priorityPeople, setPriorityPeople] = useState(
    pairsToText(config.slack.priorityPeople.map((p) => ({ id: p.userId, label: p.label })))
  );
  const [priorityChannels, setPriorityChannels] = useState(
    pairsToText(config.slack.priorityChannels.map((c) => ({ id: c.channelId, label: c.label })))
  );
  const [mentionChannels, setMentionChannels] = useState(
    pairsToText(config.slack.mentionChannels.map((c) => ({ id: c.channelId, label: c.label })))
  );
  const [linearViews, setLinearViews] = useState(
    pairsToText(config.linear.views.map((v) => ({ id: v.viewId, label: v.label })))
  );
  const [knownSenders, setKnownSenders] = useState(
    config.knownSenders.map((s) => `${s.match}|${s.label}|${s.account ?? ''}`).join('\n')
  );
  const [liveCustomerTitles, setLiveCustomerTitles] = useState(
    config.liveCustomerTitles.join('\n')
  );
  const [sharedCalendarIds, setSharedCalendarIds] = useState(
    config.calendar.sharedCalendarIds.join('\n')
  );
  // A config saved before `hiddenTitleTerms` existed shows (and, on Save,
  // writes) the same defaults the server fills in on read.
  const [hiddenTitleTerms, setHiddenTitleTerms] = useState(
    (config.calendar.hiddenTitleTerms ?? DEFAULT_HELM_SOURCE_CONFIG.calendar.hiddenTitleTerms).join(
      '\n'
    )
  );
  const [cardStyle, setCardStyle] = useState<HelmCardStyle>(config.cardStyle);
  const [readyNudge, setReadyNudge] = useState(config.readyNudge);

  const handleSave = () => {
    onSave({
      slack: {
        priorityPeople: textToPairs(priorityPeople).map((p) => ({ userId: p.id, label: p.label })),
        priorityChannels: textToPairs(priorityChannels).map((c) => ({
          channelId: c.id,
          label: c.label,
        })),
        mentionChannels: textToPairs(mentionChannels).map((c) => ({
          channelId: c.id,
          label: c.label,
        })),
      },
      linear: { views: textToPairs(linearViews).map((v) => ({ viewId: v.id, label: v.label })) },
      knownSenders: linesOf(knownSenders).map((line) => {
        const [match, label, account] = line.split('|').map((s) => (s ?? '').trim());
        return { match, label: label || match, account: account || null };
      }),
      liveCustomerTitles: linesOf(liveCustomerTitles),
      calendar: {
        sharedCalendarIds: linesOf(sharedCalendarIds),
        hiddenTitleTerms: linesOf(hiddenTitleTerms),
      },
      cardStyle,
      readyNudge,
    });
  };

  return (
    <div className="space-y-4 rounded-lg border border-zinc-200 bg-white p-4 dark:border-zinc-700 dark:bg-zinc-900">
      <div className="flex items-center justify-between">
        <h3 className="text-xs font-medium uppercase tracking-wide text-zinc-500">
          Source configuration
        </h3>
        {isDefault && (
          <span className="text-xs text-amber-600 dark:text-amber-400">using defaults</span>
        )}
      </div>

      <ConfigField
        label="Slack priority people (userId|label per line)"
        value={priorityPeople}
        onChange={setPriorityPeople}
      />
      <ConfigField
        label="Slack priority channels (channelId|label per line)"
        value={priorityChannels}
        onChange={setPriorityChannels}
      />
      <ConfigField
        label="Slack mention channels (channelId|label per line)"
        value={mentionChannels}
        onChange={setMentionChannels}
      />
      <ConfigField
        label="Linear views (viewId|label per line)"
        value={linearViews}
        onChange={setLinearViews}
      />
      <ConfigField
        label="Known senders (match|label|account per line)"
        value={knownSenders}
        onChange={setKnownSenders}
      />
      <ConfigField
        label="Extra live customer titles (one per line; accounts at stage Onboarded count already)"
        value={liveCustomerTitles}
        onChange={setLiveCustomerTitles}
      />
      <ConfigField
        label="Shared calendar IDs (one per line)"
        value={sharedCalendarIds}
        onChange={setSharedCalendarIds}
      />
      <ConfigField
        label="Hidden meeting title words (one per line)"
        value={hiddenTitleTerms}
        onChange={setHiddenTitleTerms}
      />

      <div className="space-y-4">
        <Field className="sm:max-w-xs">
          <Label className="text-xs text-zinc-500">Card style</Label>
          <Select
            data-testid="helm-config-card-style"
            value={cardStyle}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
              setCardStyle(e.target.value as HelmCardStyle)
            }
          >
            {HELM_CARD_STYLES.map((style) => (
              <option key={style} value={style}>
                {style}
              </option>
            ))}
          </Select>
        </Field>
        {/* `SwitchField`'s own control is a compact 24px/20px switch — too
            small alone as a 44px phone tap target — so the whole row gets a
            `min-h-11` floor regardless of the control's own size. */}
        <SwitchField data-testid="helm-config-ready-nudge-field" className="min-h-11 items-center">
          <Label>Send the &quot;card is ready&quot; Slack nudge</Label>
          <Switch
            data-testid="helm-config-ready-nudge"
            checked={readyNudge}
            onChange={setReadyNudge}
          />
        </SwitchField>
        <Text className="text-xs text-zinc-500">
          Applies from the next run (Refresh to apply now).
        </Text>
      </div>

      <Button data-testid="helm-config-save" disabled={saving} onClick={handleSave}>
        {saving ? 'Saving…' : 'Save'}
      </Button>
    </div>
  );
}
